import React, { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { CheckCircle2, Lock, BookOpen, MessageCircle, TrendingUp, Loader2 } from "lucide-react";
import { toast } from "sonner";

export const UpgradePanel = ({ reason }) => {
  const [program, setProgram] = useState(null);
  const [loading, setLoading] = useState(false);
  const inFlight = useRef(false);

  useEffect(() => {
    api.get("/program").then((r) => setProgram(r.data)).catch(() => {});
  }, []);

  const buy = async () => {
    if (inFlight.current) return;
    inFlight.current = true;
    setLoading(true);
    try {
      const { data } = await api.post("/payments/checkout", { origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't start checkout. Please try again.");
      inFlight.current = false;
      setLoading(false);
    }
  };

  if (!program) return <div className="p-8 text-[#5C6A61]">Loading…</div>;

  return (
    <div className="bg-white rounded-3xl border border-[#2A3B32]/10 shadow-[0_20px_60px_rgba(42,59,50,0.08)] overflow-hidden fade-up" data-testid="upgrade-panel">
      <div className="bg-[#2A3B32] text-[#FDFBF7] p-8">
        {reason && <div className="text-sm text-[#F2CC8F] mb-3" data-testid="upgrade-reason">{reason}</div>}
        <div className="eyebrow text-[#F2CC8F]">savior life</div>
        <h2 className="font-serif-display text-3xl md:text-4xl mt-2 leading-tight">{program.name}</h2>
        <p className="mt-4 text-[#FDFBF7]/85 leading-relaxed">{program.description}</p>
      </div>

      <div className="p-8 space-y-8">
        <div className="grid sm:grid-cols-3 gap-4">
          {[
            { icon: MessageCircle, t: `${program.paid_daily_limit} coaching messages a day`, d: `for ${program.coaching_days} days from purchase` },
            { icon: TrendingUp, t: "Progress tracking", d: "check off every exercise" },
            { icon: BookOpen, t: "Downloadable workbook", d: "yours to keep" },
          ].map((x) => (
            <div key={x.t} className="bg-[#F2EBE5] rounded-2xl p-4">
              <x.icon size={18} className="text-[#E07A5F]" />
              <div className="font-medium text-[#1C221D] mt-2 text-sm">{x.t}</div>
              <div className="text-xs text-[#5C6A61]">{x.d}</div>
            </div>
          ))}
        </div>

        <div className="space-y-4" data-testid="upgrade-outline">
          {program.outline.map((w) => (
            <div key={w.week} className="border border-[#2A3B32]/10 rounded-2xl p-5">
              <div className="eyebrow text-[#5C6A61]">week {w.week}</div>
              <div className="font-serif-display text-2xl text-[#1C221D]">{w.title}</div>
              <p className="text-sm text-[#5C6A61] mt-1">{w.theme}</p>
              <ul className="mt-3 grid sm:grid-cols-2 gap-2">
                {w.exercises.map((e) => (
                  <li key={e} className="flex items-center gap-2 text-sm text-[#1C221D]">
                    <Lock size={12} className="text-[#5C6A61] shrink-0" /> {e}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="border-t border-[#2A3B32]/10 pt-6 flex flex-col sm:flex-row sm:items-center justify-between gap-5">
          <div>
            <div className="font-serif-display text-4xl text-[#1C221D]" data-testid="upgrade-price">{program.price}</div>
            <div className="text-sm text-[#5C6A61] mt-1 flex items-center gap-1.5">
              <CheckCircle2 size={14} className="text-[#81B29A]" />
              One-time payment. Not a subscription. You'll never be charged again.
            </div>
            <div className="text-xs text-[#5C6A61] mt-1">
              After {program.coaching_days} days you go back to {program.free_daily_limit} free messages a day and keep the program and workbook forever.
            </div>
          </div>
          <button
            onClick={buy}
            disabled={loading}
            data-testid="buy-reset-btn"
            className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full bg-[#E07A5F] text-[#FDFBF7] font-medium hover:-translate-y-0.5 active:scale-95 transition-transform duration-200 disabled:opacity-60 shrink-0"
          >
            {loading && <Loader2 size={16} className="animate-spin" />}
            Get the Reset for {program.price}
          </button>
        </div>
      </div>
    </div>
  );
};

export default UpgradePanel;
